import { HttpClient } from '@/shared/lib/http-client';
import { env } from '@/config/env';
import { subscriptionMapper, type SubscriptionMapper } from './subscription.mapper';
import type { SubscriptionsResponseDTO } from './subscription.dto';
import type { UserSubscription } from '../types';

export interface CancelResult {
  success: boolean;
  error?: string;
}

export interface ISubscriptionsService {
  readonly isConfigured: boolean;
  getUserSubscriptions(uid: string): Promise<UserSubscription[]>;
  cancelSubscription(subscriptionId: string): Promise<CancelResult>;
}

/**
 * Subscription backend client.
 * - Read:   POST {base}/subscriptions/{uid} -> { subscriptions: [...] }
 * - Cancel: POST {origin}/api/cancel { id }  (lives at the host root, not under {base})
 */
export class SubscriptionsService extends HttpClient implements ISubscriptionsService {
  readonly isConfigured: boolean;
  private readonly mapper: SubscriptionMapper;
  private readonly cancelUrl: string;

  constructor(baseURL: string, mapper: SubscriptionMapper = subscriptionMapper) {
    super({ baseURL, headers: { 'Content-Type': 'text/plain' } });
    this.mapper = mapper;
    this.isConfigured = Boolean(baseURL);
    this.cancelUrl = baseURL ? `${new URL(baseURL).origin}/api/cancel` : '';
  }

  async getUserSubscriptions(uid: string): Promise<UserSubscription[]> {
    if (!this.isConfigured || !uid) return [];

    const res = await this.post<SubscriptionsResponseDTO>(
      `/subscriptions/${encodeURIComponent(uid)}`,
    );
    return this.mapper.toDomainList(res?.subscriptions ?? []);
  }

  async cancelSubscription(subscriptionId: string): Promise<CancelResult> {
    if (!this.isConfigured || !subscriptionId) {
      return { success: false, error: 'Cancellation is not available.' };
    }

    // Absolute URL — axios ignores baseURL when the url is absolute.
    const res = await this.post<CancelResult>(
      this.cancelUrl,
      { id: subscriptionId },
      { headers: { 'Content-Type': 'application/json' } },
    );

    if (res?.error) return { success: false, error: res.error };
    return { success: true };
  }
}

export const subscriptionsService: ISubscriptionsService = new SubscriptionsService(
  env.VITE_SUBSCRIPTIONS_API_URL,
);
